import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { keys } from './queries';
import { useScraperRunEvents } from './socket';
import type { Scraper } from './types';

/** Bridges live scraper events into the React Query cache.
 *
 *  Patches rather than invalidates: with ~20 scrapers each emitting an event on
 *  run start *and* finish, invalidation would mean a /api/scrapers round trip
 *  every second or two. setQueryData updates just the row that changed. */
export function SocketProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  useScraperRunEvents((event) => {
    queryClient.setQueryData<Scraper[]>(keys.scrapers, (prev) => {
      if (!prev) return prev;
      let matched = false;
      const next = prev.map((s) => {
        if (s.name !== event.name) return s;
        matched = true;
        // `type` is the discriminant, not part of the Scraper shape.
        const { type: _type, ...patch } = event;
        return { ...s, ...patch };
      });
      // A scraper we don't know about yet (just added) — let the poll pick it up.
      return matched ? next : prev;
    });
  });

  return <>{children}</>;
}
